<template>
  <v-container fluid class="pa-0">
    <v-card>
      <div class="text-subtitle-1 font-weight-thin px-4 pt-3">
        Referral fees paid to other firms in the selected period
      </div>
      <v-row>
        <v-col cols="12" sm="6" md="3">
          <div class="fee-stat-container">
            <div class="fee-stat-title">Total Paid</div>
            <div class="fee-stat-data">{{ formatMoney(totalFee) }}</div>
          </div>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <div class="fee-stat-container">
            <div class="fee-stat-title">Payments</div>
            <div class="fee-stat-data">{{ fees.length }}</div>
          </div>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <div class="fee-stat-container">
            <div class="fee-stat-title">Firms</div>
            <div class="fee-stat-data">{{ firmCount }}</div>
          </div>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <div class="fee-stat-container">
            <div class="fee-stat-title">Cases</div>
            <div class="fee-stat-data">{{ caseCount }}</div>
          </div>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12" lg="4">
          <v-card outlined class="mb-4">
            <v-card-title class="pb-2">
              <span class="text-h6">By Firm</span>
            </v-card-title>
            <v-data-table
              :headers="breakdownHeaders('Firm')"
              :items="byFirm"
              dense
              :items-per-page="10"
              :sort-by="['fee']"
              :sort-desc="[true]"
              :loading="$apollo.loading"
            >
              <template v-slot:item.fee="{ item }">
                {{ formatMoney(item.fee) }}
              </template>
            </v-data-table>
          </v-card>
        </v-col>
        <v-col cols="12" lg="4">
          <v-card outlined class="mb-4">
            <v-card-title class="pb-2">
              <span class="text-h6">By Case Group</span>
            </v-card-title>
            <v-data-table
              :headers="breakdownHeaders('Case Group')"
              :items="byCaseGroup"
              dense
              :items-per-page="10"
              :sort-by="['fee']"
              :sort-desc="[true]"
              :loading="$apollo.loading"
            >
              <template v-slot:item.fee="{ item }">
                {{ formatMoney(item.fee) }}
              </template>
            </v-data-table>
          </v-card>
        </v-col>
        <v-col cols="12" lg="4">
          <v-card outlined class="mb-4">
            <v-card-title class="pb-2">
              <span class="text-h6">By Office</span>
            </v-card-title>
            <v-data-table
              :headers="breakdownHeaders('Office')"
              :items="byOffice"
              dense
              :items-per-page="10"
              :sort-by="['fee']"
              :sort-desc="[true]"
              :loading="$apollo.loading"
            >
              <template v-slot:item.fee="{ item }">
                {{ formatMoney(item.fee) }}
              </template>
            </v-data-table>
          </v-card>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12" md="6" class="d-flex align-center">
          <div class="text-h6 pl-4">Payments</div>
        </v-col>
        <v-col cols="12" md="6">
          <v-text-field
            v-model="search"
            append-icon="mdi-magnify"
            label="Search"
            hide-details
            single-line
          ></v-text-field>
        </v-col>
      </v-row>

      <v-data-table
        :headers="headers"
        :items="fees"
        :search="search"
        item-key="id"
        :loading="$apollo.loading"
        :footer-props="{
          'items-per-page-options': [25, 50, 100, -1]
        }"
        :sort-by="['receivedDate']"
        :sort-desc="[true]"
      >
        <template v-slot:item.caseId="{ item }">
          <a
            class="go-to-case-link"
            target="_blank"
            :href="
              `https://cs-web.airdesksolutions.com/SA/CaseSummary.aspx?CaseID=${item.caseId}`
            "
          >
            <v-icon size="15">mdi-open-in-new</v-icon>
          </a>
        </template>
        <template v-slot:item.fee="{ item }">
          {{ formatMoney(item.fee) }}
        </template>
      </v-data-table>
    </v-card>
  </v-container>
</template>

<script>
import gql from "graphql-tag";
import { mapGetters } from "vuex";
import accounting from "accounting";
import groupBy from "lodash/groupBy";
import uniq from "lodash/uniq";

export default {
  data() {
    return {
      search: ""
    };
  },
  apollo: {
    organization: {
      query: gql`
        query($id: ID!, $startDate: String!, $endDate: String!) {
          organization(id: $id) {
            id
            sa_referral_fees_paid(startDate: $startDate, endDate: $endDate) {
              id
              fee
              receivedDate
              firm
              caseId
              caseNumber
              clientName
              caseGroup
              office
            }
          }
        }
      `,
      variables() {
        return {
          id: this.$route.params.id,
          startDate: this.startDate,
          endDate: this.endDate
        };
      },
      fetchPolicy: "no-cache",
      skip() {
        return this.startDate === null || this.endDate === null;
      }
    }
  },
  computed: {
    ...mapGetters(["startDate", "endDate"]),
    fees() {
      if (!this.organization) return [];
      return this.organization.sa_referral_fees_paid || [];
    },
    totalFee() {
      return this.fees.reduce((sum, fee) => sum + fee.fee, 0);
    },
    firmCount() {
      return uniq(this.fees.map(fee => fee.firm)).length;
    },
    caseCount() {
      return uniq(this.fees.map(fee => fee.caseId)).length;
    },
    byFirm() {
      return this.breakdown("firm");
    },
    byCaseGroup() {
      return this.breakdown("caseGroup");
    },
    byOffice() {
      return this.breakdown("office");
    },
    headers() {
      return [
        { text: "", value: "caseId", sortable: false, width: "48px" },
        { text: "Date", value: "receivedDate" },
        { text: "Paid To", value: "firm" },
        { text: "Client", value: "clientName" },
        { text: "Case #", value: "caseNumber" },
        { text: "Case Group", value: "caseGroup" },
        { text: "Office", value: "office" },
        { text: "Fee", value: "fee" }
      ];
    }
  },
  methods: {
    formatMoney(val) {
      return accounting.formatMoney(val || 0);
    },
    breakdownHeaders(label) {
      return [
        { text: label, value: "name" },
        { text: "Fee", value: "fee" },
        { text: "Payments", value: "payments" },
        { text: "Cases", value: "cases" },
        { text: "Share", value: "share" }
      ];
    },
    breakdown(key) {
      const groups = groupBy(this.fees, fee => fee[key] || "Unspecified");
      return Object.keys(groups)
        .map(name => {
          const items = groups[name];
          const fee = items.reduce((sum, item) => sum + item.fee, 0);
          return {
            name,
            fee,
            payments: items.length,
            cases: uniq(items.map(item => item.caseId)).length,
            share:
              this.totalFee > 0
                ? ((fee / this.totalFee) * 100).toFixed(1) + "%"
                : "0%"
          };
        })
        .sort((a, b) => b.fee - a.fee);
    }
  }
};
</script>

<style scoped>
.v-container {
  width: 100% !important;
  max-width: 100% !important;
  margin: 0 !important;
  padding: 0 !important;
}
.fee-stat-container {
  padding: 8px 16px;
  text-align: center;
}
.fee-stat-title {
  font-size: 16px;
}
.fee-stat-data {
  font-size: 30px;
  font-weight: bold;
}
</style>
